import React, { useState, useEffect } from 'react';
import { FiClock, FiBriefcase, FiPlay, FiSquare, FiCoffee, FiUsers, FiCheck, FiLogOut, FiClipboard } from 'react-icons/fi';
import { getJobs, getActiveTimeEntries, getAuthToken, getTeamMembers } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config';
import axios from 'axios';
import FieldChecklistModal from '../components/FieldChecklistModal';
import RequiredCommentModal, { FIELD_WORK_TYPES } from '../components/RequiredCommentModal';
import { isMobileLayout } from '../utils/deviceLayout';
import { applyQueuedTimeState } from '../services/offlineTimeState';
import { subscribe as subscribeToOutbox } from '../services/outbox';

// Helper to get axios config with auth token
const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

function TimeTracking() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const [dailyEntry, setDailyEntry] = useState(null);
  const [jobEntries, setJobEntries] = useState([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedWorkType, setSelectedWorkType] = useState('Field Work');
  const [loading, setLoading] = useState(true);
  const [onBreak, setOnBreak] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isMobile, setIsMobile] = useState(isMobileLayout);
  const [showJobPicker, setShowJobPicker] = useState(false);
  
  // Job search state
  const [jobSearch, setJobSearch] = useState('');
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  
  // Crew selection state
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedCrewMembers, setSelectedCrewMembers] = useState([]);
  const [allJobEntries, setAllJobEntries] = useState([]);
  
  // Work order state
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState('');
  const [jobPhases, setJobPhases] = useState([]);        // live phases of a phased job
  const [selectedPhase, setSelectedPhase] = useState('');
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);

  // Field checklist modals
  const [fieldChecklistJob, setFieldChecklistJob] = useState(null); // clock-out mode: { job_id, job_number, job_status, entry_id }
  const [fieldChecklistOverview, setFieldChecklistOverview] = useState(null); // timer-start overview: { job_id, job_number }
  const [fieldChecklistEdit, setFieldChecklistEdit] = useState(null); // mid-job edit: { job_id, job_number }
  // Job status update modal (shown after field checklist or directly for non-field work)
  const [statusUpdateJob, setStatusUpdateJob] = useState(null); // { job_id, job_number, job_status }
  const [commentPrompt, setCommentPrompt] = useState(null);     // required note after stop: { job_id, job_number }
  const [commentQueue, setCommentQueue] = useState([]);         // remaining jobs needing a note (end-of-day)
  const [dailyClockOutPending, setDailyClockOutPending] = useState(false);

  // Full list of job statuses shown in the status update modal after clock-out.
  // Mirrors components/JobForm.js statusOptions (minus Imported, which is never set manually).
  const jobStatusOptions = [
    { status: 'Pending', label: 'Pending', color: '#94a3b8' },
    { status: 'Scheduled', label: 'Scheduled', color: '#3b82f6' },
    { status: 'In Progress - Office Work', label: 'Office Work (pre-field)', color: '#0ea5e9' },
    { status: 'In Progress - Initial Field Work', label: 'Initial Field Work', color: '#a78bfa' },
    { status: 'In Progress - Topo', label: 'Topo', color: '#6366f1' },
    { status: 'In Progress - Additional Field Work Required', label: 'Additional Field Work Required', color: '#7c3aed' },
    { status: 'In Progress - Drafting', label: 'Ready for Drafting', color: '#8b5cf6' },
    { status: 'Survey Complete', label: 'Survey Complete', color: '#34d399' },
    { status: 'Needs Review', label: 'Needs Review', color: '#f97316' },
    { status: 'Sent for Review', label: 'Sent for Review', color: '#fb923c' },
    { status: 'Approved - Internal Review', label: 'Approved (our office)', color: '#4ade80' },
    { status: 'Approved - Planning & Zoning', label: 'Approved (Planning & Zoning)', color: '#22c55e' },
    { status: 'Submitted for Recording', label: 'Submitted for Recording', color: '#14b8a6' },
    { status: 'Recorded', label: 'Recorded', color: '#0d9488' },
    { status: 'Final Mark Up / Stake Lines', label: 'Final Mark Up / Stake Lines', color: '#ec4899' },
    { status: 'Needs Invoice', label: 'Needs Invoice', color: '#eab308' },
    { status: 'Invoiced', label: 'Invoiced', color: '#f59e0b' },
    { status: 'Paid', label: 'Paid', color: '#16a34a' },
  ];

  const workTypes = [
    { value: 'Field Work', label: '🌲 Field Work', description: 'On-site surveying' },
    { value: 'Drafting', label: '📐 Drafting', description: 'CAD/Drawing work' },
    { value: 'Final Mark Up / Stake Lines', label: '📍 Stake Lines', description: 'Final mark up work' },
    { value: 'Office Work', label: '🏢 Office Work', description: 'General office tasks' },
    { value: 'Research', label: '🔍 Research', description: 'Title/deed research' },
    { value: 'Travel', label: '🚗 Travel', description: 'Driving to/from site' },
    { value: 'Review', label: '✅ Review', description: 'QC/Review work' },
  ];

  // Filter jobs based on search - require at least 2 chars to start filtering
  const filteredJobs = (!jobSearch || jobSearch.length < 2) ? [] : jobs
    .filter(job => job.status !== 'Completed' && job.status !== 'Paid')
    .filter(job => {
      const search = jobSearch.toLowerCase();
      return (
        (job.job_number && job.job_number.toLowerCase().includes(search)) ||
        (job.client_name && job.client_name.toLowerCase().includes(search)) ||
        (job.property_address && job.property_address.toLowerCase().includes(search))
      );
    });

  const handleJobSelect = (job) => {
    setSelectedJob(job.id.toString());
    setJobSearch(job.job_number + ' - ' + (job.client_name || job.property_address || ''));
    setShowJobDropdown(false);
    loadWorkOrders(job.id);
    loadPhases(job);
  };

  const loadPhases = async (job) => {
    setSelectedPhase('');
    if (!job?.has_phases) { setJobPhases([]); return; }
    try {
      const config = await getAuthConfig();
      const res = await axios.get(`${API_URL}/jobs/${job.id}/phases`, config);
      // Only phases still being worked (hide Paid/Complete/Cancelled).
      const live = (res.data?.phases || []).filter(p => !['Paid', 'Cancelled', 'Complete', 'Survey Complete'].includes(p.status));
      setJobPhases(live);
      if (live.length === 1) setSelectedPhase(String(live[0].id));
    } catch (e) { setJobPhases([]); }
  };

  // Once a phase is chosen, only show that phase's work orders (plus any with no phase).
  const visibleWorkOrders = selectedPhase
    ? workOrders.filter(w => !w.phase_id || String(w.phase_id) === String(selectedPhase))
    : workOrders;

  useEffect(() => {
    // Orientation-stable on purpose — see utils/deviceLayout. Keying off
    // innerWidth swapped this page between its mobile and desktop trees when a
    // phone was rotated, remounting the required-note modal mid-answer.
    const checkMobile = () => setIsMobile(isMobileLayout());

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadJobs();
    loadActiveEntries();
    loadTeamMembers();

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, [user]);

  // Re-read the clock whenever the offline queue changes — a punch queuing up, or
  // draining once signal returns, both change what "clocked in" means here.
  useEffect(() => subscribeToOutbox(() => loadActiveEntries()), [user]);

  const loadJobs = async () => {
    try {
      // The job picker needs most jobs, so the win here is payload size, not row count:
      // fields=lite drops work orders, change orders, phases, invoices and time entries
      // that this page never reads. Status filtering moves to the server too.
      const params = new URLSearchParams({
        status_not_in: 'Invoiced,Paid',
        fields: 'lite',
      });
      const response = await getJobs(params.toString());
      const jobsData = response.data.jobs || response.data || [];
      setJobs(jobsData);
      setJobsLoaded(true);
    } catch (error) {
      console.error('Error loading jobs:', error);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await getTeamMembers();
      const members = response.data.team_members || response.data || [];
      setTeamMembers(members);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  const loadActiveEntries = async () => {
    try {
      const response = await getActiveTimeEntries();
      const serverEntries = response.data.time_entries || response.data || [];
      // Fold in punches still queued offline. Without this a crew who clocked in
      // with no signal shows as clocked OUT and can't clock out later.
      const entries = await applyQueuedTimeState(serverEntries, user?.id);

      // Store all active job entries (for checking who's already clocked in)
      setAllJobEntries(entries.filter(e => e.job_id));
      
      const myEntries = entries.filter(e => e.team_member_id === user?.id);
      
      const daily = myEntries.find(e => !e.job_id);
      
      // Group job entries by clock_in_group (or by entry id if no group)
      const jobEntriesWithGroup = entries.filter(e => e.job_id);
      const groupedJobs = {};
      
      for (const entry of jobEntriesWithGroup) {
        const groupKey = entry.clock_in_group || `solo_${entry.id}`;
        
        // Only include groups where current user is a member
        const userInGroup = jobEntriesWithGroup.some(e => 
          (e.clock_in_group === entry.clock_in_group || e.id === entry.id) && 
          e.team_member_id === user?.id
        );
        
        if (!userInGroup && !entry.clock_in_group) continue;
        if (!userInGroup && entry.clock_in_group) {
          // Check if user is part of this group
          const myGroupEntry = jobEntriesWithGroup.find(e => 
            e.clock_in_group === entry.clock_in_group && e.team_member_id === user?.id
          );
          if (!myGroupEntry) continue;
        }
        
        if (!groupedJobs[groupKey]) {
          groupedJobs[groupKey] = {
            ...entry,
            crew_members: [],
            my_entry_id: null,
            my_is_paused: false,
            my_accumulated_seconds: 0
          };
        }
        groupedJobs[groupKey].crew_members.push({
          id: entry.team_member_id,
          name: entry.member_name || entry.team_member_name,
          entry_id: entry.id
        });
        if (entry.team_member_id === user?.id) {
          groupedJobs[groupKey].my_entry_id = entry.id;
          groupedJobs[groupKey].my_is_paused = entry.is_paused;
          groupedJobs[groupKey].my_accumulated_seconds = entry.accumulated_seconds || 0;
          groupedJobs[groupKey].my_clock_in = entry.clock_in;
          groupedJobs[groupKey].my_calc_clock_in = entry.calc_clock_in || entry.clock_in;
        }
      }
      
      setDailyEntry(daily || null);
      // Set break status from API response
      setOnBreak(daily?.on_break || false);
      setJobEntries(Object.values(groupedJobs));
      setLoading(false);
    } catch (error) {
      console.error('Error loading active entries:', error);
      setLoading(false);
    }
  };

  const loadWorkOrders = async (jobId) => {
    if (!jobId) {
      setWorkOrders([]);
      setSelectedWorkOrder('');
      return;
    }
    
    setLoadingWorkOrders(true);
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/jobs/${jobId}/work-orders`, config);
      const orders = response.data.work_orders || [];
      setWorkOrders(orders);
      // Auto-select if only one work order
      if (orders.length === 1) {
        setSelectedWorkOrder(orders[0].id.toString());
      } else {
        setSelectedWorkOrder('');
      }
    } catch (error) {
      console.error('Error loading work orders:', error);
      setWorkOrders([]);
    }
    setLoadingWorkOrders(false);
  };

  const handleDailyClockIn = async () => {
    if (!user) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-entries/clock-in`, {
        team_member_id: user.id,
        job_id: null,
        work_type: 'Daily',
        is_billable: false,
        notes: 'Daily clock in'
      }, config);
      loadActiveEntries();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to clock in');
    }
  };

  const finishDailyClockOut = async () => {
    setDailyClockOutPending(false);
    if (!dailyEntry) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-entries/${dailyEntry.id}/clock-out`, {}, config);
      setDailyEntry(null);
      setJobEntries([]);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to clock out');
    }
  };

  const handleDailyClockOut = async () => {
    if (!dailyEntry) return;

    if (jobEntries.length > 0) {
      if (!window.confirm('You have active job timers. Clock out of all and end your day?')) {
        return;
      }
      const config = await getAuthConfig();
      const queue = [];
      for (const entry of jobEntries) {
        try { await axios.post(`${API_URL}/time-entries/${entry.id}/clock-out`, {}, config); } catch (e) { /* keep going */ }
        if (entry.job_id) queue.push({ job_id: entry.job_id, job_number: entry.job_number, work_type: entry.work_type });
      }
      loadActiveEntries();
      if (queue.length > 0) {
        // Require an end-of-visit note for each job before the day closes out.
        setDailyClockOutPending(true);
        setCommentQueue(queue.slice(1));
        setCommentPrompt(queue[0]);
        return;
      }
    }

    await finishDailyClockOut();
  };

  const handleStartBreak = async () => {
    if (!dailyEntry) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-entries/${dailyEntry.id}/start-break`, {}, config);
      setOnBreak(true);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to start break');
    }
  };

  const handleEndBreak = async () => {
    if (!dailyEntry) return;
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-entries/${dailyEntry.id}/end-break`, {}, config);
      setOnBreak(false);
      loadActiveEntries(); // Refresh to get updated break minutes
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to end break');
    }
  };

  const handlePauseJob = async (entryId) => {
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-entries/${entryId}/pause`, {}, config);
      loadActiveEntries(); // Refresh to show paused state
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to pause timer');
    }
  };

  const handleResumeJob = async (entryId) => {
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-entries/${entryId}/resume`, {}, config);
      loadActiveEntries(); // Refresh to show resumed state
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to resume timer');
    }
  };

  const handlePauseCrew = async (jobId) => {
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-entries/pause-crew/${jobId}`, {}, config);
      loadActiveEntries(); // Refresh to show paused state
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to pause crew');
    }
  };

  const handleResumeCrew = async (jobId) => {
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-entries/resume-crew/${jobId}`, {}, config);
      loadActiveEntries(); // Refresh to show resumed state
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to resume crew');
    }
  };

  const handleJobStart = async () => {
    if (!user || !selectedJob) return;
    
    if (jobEntries.some(e => e.job_id === parseInt(selectedJob))) {
      alert('Already tracking time for this job');
      return;
    }
    
    const job = jobs.find(j => j.id === parseInt(selectedJob));
    const workOrder = workOrders.find(wo => wo.id === parseInt(selectedWorkOrder));
    if (jobPhases.length > 1 && !selectedPhase) {
      alert('This job has phases — pick which phase you\'re working so your time bills to the right client.');
      return;
    }
    
    // Build list of team members to clock in (always include self)
    const memberIds = [user.id, ...selectedCrewMembers.filter(id => id !== user.id)];
    
    try {
      const config = await getAuthConfig();
      
      // Use batch endpoint to clock in multiple people
      await axios.post(`${API_URL}/time-entries/clock-in-crew`, {
        team_member_ids: memberIds,
        job_id: parseInt(selectedJob),
        work_order_id: selectedWorkOrder ? parseInt(selectedWorkOrder) : null,
        phase_id: selectedPhase ? parseInt(selectedPhase) : null,
        work_type: selectedWorkType,
        is_billable: true,
        notes: workOrder 
          ? `${selectedWorkType} on ${job?.job_number} - ${workOrder.order_number}: ${workOrder.description}`
          : `${selectedWorkType} on ${job?.job_number}`
      }, config);
      
      setSelectedJob('');
      setSelectedWorkOrder('');
      setWorkOrders([]);
      setJobPhases([]);
      setSelectedPhase('');
      setSelectedCrewMembers([]);
      setShowJobPicker(false);
      loadActiveEntries();

      // Show field checklist overview for field work types
      if (FIELD_WORK_TYPES.includes(selectedWorkType) && job) {
        setFieldChecklistOverview({ job_id: job.id, job_number: job.job_number });
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to start job timer');
    }
  };

  const toggleCrewMember = (memberId) => {
    if (memberId === user?.id) return; // Can't deselect self
    
    setSelectedCrewMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  // Check if a team member is already clocked into the selected job
  const isAlreadyClockedIn = (memberId, jobId) => {
    return allJobEntries.some(e => e.team_member_id === memberId && e.job_id === parseInt(jobId));
  };

  // Check if a team member is on ANY active job timer
  const isOnAnyActiveJob = (memberId) => {
    return allJobEntries.some(e => e.team_member_id === memberId);
  };

  // Get available crew members (exclude those already on ANY active job)
  const getAvailableCrewMembers = () => {
    return teamMembers.filter(m => 
      m.id !== user?.id && !isOnAnyActiveJob(m.id)
    );
  };

  // Helper: decide whether to show field checklist or go straight to status update
  const showPostClockOutFlow = (jobEntry, entryId) => {
    if (!jobEntry) return;
    const isFieldWork = FIELD_WORK_TYPES.includes(jobEntry.work_type);
    if (isFieldWork) {
      // Show field departure checklist first
      setFieldChecklistJob({
        job_id: jobEntry.job_id,
        job_number: jobEntry.job_number,
        job_status: jobEntry.job_status,
        work_type: jobEntry.work_type,
        entry_id: entryId || null,
      });
    } else {
      // Skip to status update for non-field work (drafting, office, etc.)
      setStatusUpdateJob({
        job_id: jobEntry.job_id,
        job_number: jobEntry.job_number,
        job_status: jobEntry.job_status,
        work_type: jobEntry.work_type,
      });
    }
  };

  // Called when field checklist is completed or skipped.
  // Uses functional setState so we always read the latest value, even if the
  // callback fires from a stale closure (e.g., after the checklist modal
  // re-renders several times during its save flow).
  const handleFieldChecklistDone = () => {
    setFieldChecklistJob(currentJob => {
      if (currentJob) {
        setStatusUpdateJob({
          job_id: currentJob.job_id,
          job_number: currentJob.job_number,
          job_status: currentJob.job_status,
          work_type: currentJob.work_type,
        });
      }
      return null;
    });
  };

  const handleJobStop = async (jobId) => {
    try {
      const jobEntry = jobEntries.find(e => e.job_id === jobId) || allJobEntries.find(e => e.job_id === jobId);
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-entries/clock-out-job/${jobId}`, {}, config);
      loadActiveEntries();
      if (jobEntry) {
        showPostClockOutFlow(jobEntry, jobEntry.id);
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to stop job timer');
    }
  };

  const handleLeaveJob = async (entryId) => {
    try {
      const entry = jobEntries.find(e => e.id === entryId) || allJobEntries.find(e => e.id === entryId);
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/time-entries/${entryId}/clock-out`, {}, config);
      loadActiveEntries();
      if (entry?.job_id) {
        showPostClockOutFlow(entry, entryId);
      }
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to leave job');
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!statusUpdateJob) return;
    const j = statusUpdateJob;
    try {
      const config = await getAuthConfig();
      await axios.put(`${API_URL}/jobs/${j.job_id}`, { status: newStatus }, config);
    } catch (error) {
      console.error('Failed to update job status:', error);
    }
    goToCommentPrompt(j);
  };

  // Close the status modal and require an end-of-visit note for the job.
  const goToCommentPrompt = (j) => {
    setStatusUpdateJob(null);
    if (j) setCommentPrompt({ job_id: j.job_id, job_number: j.job_number, work_type: j.work_type });
  };

  // Advances the end-of-day note queue if a daily clock-out is in progress.
  const handleCommentDone = () => {
    if (!dailyClockOutPending) { setCommentPrompt(null); return; }
    if (commentQueue.length > 0) {
      setCommentPrompt(commentQueue[0]);
      setCommentQueue(commentQueue.slice(1));
    } else {
      setCommentPrompt(null);
      finishDailyClockOut();
    }
  };

  const calculateElapsedTime = (clockIn, entry = null) => {
    if (!clockIn) return '00:00:00';
    
    let diff = 0;
    
    // If entry is paused, just show accumulated time
    // Check both is_paused (individual) and my_is_paused (grouped)
    const isPaused = entry?.my_is_paused || entry?.is_paused;
    const accumulatedSeconds = entry?.my_accumulated_seconds || entry?.accumulated_seconds || 0;
    
    if (isPaused) {
      diff = accumulatedSeconds;
    } else {
      // Use calc_clock_in for timer (actual clock_in after resume), not display clock_in
      let timestamp = entry?.my_calc_clock_in || entry?.calc_clock_in || clockIn;
      if (!timestamp.endsWith('Z') && !timestamp.includes('+')) {
        timestamp = timestamp + 'Z';
      }
      
      const start = new Date(timestamp);
      const now = new Date();
      diff = Math.floor((now - start) / 1000);
      
      // Add accumulated seconds (from previous pause/resume cycles)
      if (accumulatedSeconds) {
        diff += accumulatedSeconds;
      }
    }
    
    // Handle negative diff (shouldn't happen but just in case)
    if (diff < 0) return '00:00:00';
    
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Please log in to use the time clock.
      </div>
    );
  }

  // Mobile Layout
  if (isMobile) {
    return (
      <div style={{ padding: '5px' }}>
        {/* Current Time Display */}
        <div style={{
          textAlign: 'center',
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{ 
            fontSize: '36px', 
            fontWeight: '700',
            fontFamily: 'monospace',
            color: '#333'
          }}>
            {currentTime.toLocaleTimeString()}
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
            {currentTime.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'short', 
              day: 'numeric' 
            })}
          </div>
        </div>

        {/* Daily Clock Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '15px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ 
            margin: '0 0 15px', 
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FiClock /> Daily Time
          </h2>

          {!dailyEntry ? (
            <button
              onClick={handleDailyClockIn}
              style={{
                width: '100%',
                padding: '20px',
                fontSize: '18px',
                fontWeight: '600',
                backgroundColor: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                minHeight: '60px'
              }}
            >
              <FiPlay size={24} /> Start Day
            </button>
          ) : (
            <div>
              {/* Timer Display */}
              <div style={{
                textAlign: 'center',
                padding: '20px',
                backgroundColor: '#f0fdf4',
                borderRadius: '12px',
                marginBottom: '15px'
              }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                  {onBreak ? '☕ On Break' : '⏱️ Working'}
                </div>
                <div style={{ 
                  fontSize: '32px', 
                  fontFamily: 'monospace',
                  fontWeight: '700',
                  color: onBreak ? '#f59e0b' : '#22c55e'
                }}>
                  {calculateElapsedTime(dailyEntry.clock_in)}
                </div>
              </div>

              {/* Break Button */}
              {!onBreak ? (
                <button
                  onClick={handleStartBreak}
                  style={{
                    width: '100%',
                    padding: '14px',
                    fontSize: '16px',
                    backgroundColor: '#fef3c7',
                    color: '#92400e',
                    border: '1px solid #fcd34d',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '10px',
                    minHeight: '50px'
                  }}
                >
                  <FiCoffee /> Take Break
                </button>
              ) : (
                <button
                  onClick={handleEndBreak}
                  style={{
                    width: '100%',
                    padding: '14px',
                    fontSize: '16px',
                    backgroundColor: '#22c55e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '10px',
                    minHeight: '50px'
                  }}
                >
                  <FiPlay /> End Break
                </button>
              )}

              {/* End Day Button */}
              <button
                onClick={handleDailyClockOut}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '16px',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: '1px solid #fecaca',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  minHeight: '50px'
                }}
              >
                <FiSquare /> End Day
              </button>
            </div>
          )}
        </div>

        {/* Job Time Card */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <h2 style={{ 
            margin: '0 0 15px', 
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FiBriefcase /> Job Time
          </h2>

          {/* Show active job timers first if user is on any */}
          {jobEntries.length > 0 && (
            <div style={{ marginBottom: '15px' }}>
              {jobEntries.map(entry => (
                <div 
                  key={entry.clock_in_group || entry.id}
                  style={{
                    padding: '14px',
                    backgroundColor: entry.my_is_paused ? '#fef3c7' : (entry.work_order_id ? '#f5f3ff' : '#e0f2fe'),
                    borderRadius: '12px',
                    marginBottom: '10px',
                    border: entry.my_is_paused ? '2px solid #f59e0b' : (entry.work_order_id ? '2px solid #7c3aed' : 'none')
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            fontWeight: '600', 
                            color: entry.work_order_id ? '#7c3aed' : '#0369a1',
                            fontSize: '15px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            {entry.job_number}
                            {entry.work_order_number && (
                              <span style={{ 
                                fontSize: '12px', 
                                fontWeight: '500'
                              }}>
                                ({entry.work_order_number})
                              </span>
                            )}
                            {entry.my_is_paused && (
                              <span style={{
                                fontSize: '11px',
                                color: '#f59e0b',
                                backgroundColor: '#fef3c7',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontWeight: '600'
                              }}>
                                ⏸️ PAUSED
                              </span>
                            )}
                          </div>
                          {entry.work_order_description && (
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#7c3aed',
                              marginTop: '2px'
                            }}>
                              📋 {entry.work_order_description}
                            </div>
                          )}
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#666',
                            marginTop: '2px'
                          }}>
                            {entry.work_type}
                          </div>
                          <div style={{ 
                            fontSize: '22px', 
                            fontFamily: 'monospace',
                            fontWeight: '600',
                            color: entry.my_is_paused ? '#f59e0b' : (entry.work_order_id ? '#7c3aed' : '#0284c7'),
                            marginTop: '4px'
                          }}>
                            {calculateElapsedTime(entry.clock_in, entry)}
                          </div>
                          {/* Show crew members */}
                          {entry.crew_members && entry.crew_members.length > 0 && (
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#475569',
                              marginTop: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              flexWrap: 'wrap'
                            }}>
                              <FiUsers size={12} />
                              {entry.crew_members.map((m, i) => (
                                <span key={m.id}>
                                  {m.name}{i < entry.crew_members.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {/* Pause Crew / Resume Crew button - show when multiple crew members */}
                          {entry.crew_members && entry.crew_members.length > 1 && (
                            entry.my_is_paused ? (
                              <button
                                onClick={() => handleResumeCrew(entry.job_id)}
                                style={{
                                  padding: '12px 20px',
                                  backgroundColor: '#22c55e',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '10px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  minHeight: '44px'
                                }}
                              >
                                <FiPlay /> Resume Crew
                              </button>
                            ) : (
                              <button
                                onClick={() => handlePauseCrew(entry.job_id)}
                                style={{
                                  padding: '12px 20px',
                                  backgroundColor: '#f59e0b',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '10px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  minHeight: '44px'
                                }}
                              >
                                <FiCoffee /> Pause Crew
                              </button>
                            )
                          )}
                          {/* Solo Pause/Resume button - show when single person */}
                          {entry.my_entry_id && (!entry.crew_members || entry.crew_members.length <= 1) && (
                            entry.my_is_paused ? (
                              <button
                                onClick={() => handleResumeJob(entry.my_entry_id)}
                                style={{
                                  padding: '12px 20px',
                                  backgroundColor: '#22c55e',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '10px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  minHeight: '44px'
                                }}
                              >
                                <FiPlay /> Resume
                              </button>
                            ) : (
                              <button
                                onClick={() => handlePauseJob(entry.my_entry_id)}
                                style={{
                                  padding: '12px 20px',
                                  backgroundColor: '#f59e0b',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '10px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  minHeight: '44px'
                                }}
                              >
                                <FiCoffee /> Pause
                              </button>
                            )
                          )}
                          {FIELD_WORK_TYPES.includes(entry.work_type) && (
                            <button
                              onClick={() => setFieldChecklistEdit({ job_id: entry.job_id, job_number: entry.job_number })}
                              style={{
                                padding: '12px 16px', backgroundColor: '#eff6ff',
                                color: '#2563eb', border: '1px solid #bfdbfe',
                                borderRadius: '10px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '6px',
                                fontSize: '14px', fontWeight: '500', minHeight: '44px'
                              }}
                            >
                              <FiClipboard /> Checklist
                            </button>
                          )}
                          <button
                            onClick={() => handleJobStop(entry.job_id)}
                            style={{
                              padding: '12px 20px',
                              backgroundColor: '#dc2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              fontSize: '14px',
                              fontWeight: '500',
                              minHeight: '44px'
                            }}
                          >
                            <FiSquare /> Stop Crew
                          </button>
                          {entry.my_entry_id && (
                            <button
                              onClick={() => handleLeaveJob(entry.my_entry_id)}
                              style={{
                                padding: '10px 16px',
                                backgroundColor: '#f59e0b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                fontSize: '13px',
                                fontWeight: '500',
                                minHeight: '40px'
                              }}
                            >
                              <FiLogOut /> Leave
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Start Job Button - only show if daily entry exists */}
              {dailyEntry ? (
                <button
                  onClick={() => setShowJobPicker(true)}
                  style={{
                    width: '100%',
                    padding: '16px',
                    fontSize: '16px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    minHeight: '54px'
                  }}
                >
                  <FiPlay /> Start Job Timer
                </button>
              ) : jobEntries.length === 0 && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '30px',
                  color: '#999',
                  backgroundColor: '#f8fafc',
                  borderRadius: '12px'
                }}>
                  <FiBriefcase size={32} style={{ opacity: 0.3, marginBottom: '10px' }} />
                  <p style={{ margin: 0 }}>Start your day first</p>
                </div>
              )}
        </div>

        {/* Job Picker Modal */}
        {showJobPicker && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: '70px',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'flex-end'
          }}>
            <div style={{
              backgroundColor: 'white',
              width: '100%',
              borderRadius: '20px 20px 0 0',
              padding: '15px 20px 20px',
              maxHeight: '55vh',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Header - fixed at top */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px',
                flexShrink: 0
              }}>
                <h3 style={{ margin: 0, fontSize: '17px' }}>Start Job Timer</h3>
                <button
                  onClick={() => {
                    setShowJobPicker(false);
                    setSelectedCrewMembers([]);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '15px',
                    color: '#2563eb',
                    cursor: 'pointer',
                    padding: '8px'
                  }}
                >
                  Cancel
                </button>
              </div>

              {/* Scrollable content area */}
              <div style={{ flex: 1, overflowY: 'auto', marginBottom: '10px' }}>
              {/* Job Select */}
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontWeight: '500',
                fontSize: '13px',
                color: '#555'
              }}>
                Select Job
              </label>
              <div style={{ position: 'relative', marginBottom: '10px' }}>
                <input
                  type="text"
                  value={jobSearch}
                  onChange={(e) => {
                    setJobSearch(e.target.value);
                    setShowJobDropdown(true);
                    if (!e.target.value) {
                      setSelectedJob('');
                    }
                  }}
                  onFocus={() => setShowJobDropdown(true)}
                  placeholder="Type to search jobs..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #ddd',
                    fontSize: '15px',
                    minHeight: '44px'
                  }}
                  autoComplete="off"
                />
                {showJobDropdown && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '10px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1001,
                    marginTop: '5px'
                  }}>
                    {filteredJobs.length > 0 ? (
                      filteredJobs.slice(0, 15).map(job => {
                        const isActive = jobEntries.some(e => e.job_id === job.id);
                        return (
                          <div
                            key={job.id}
                            onClick={() => {
                              if (!isActive) {
                                handleJobSelect(job);
                                setSelectedCrewMembers([]);
                              }
                            }}
                            style={{
                              padding: '14px',
                              borderBottom: '1px solid #eee',
                              backgroundColor: isActive ? '#f5f5f5' : 'white',
                              opacity: isActive ? 0.6 : 1
                            }}
                          >
                            <div style={{ fontWeight: '500', display: 'flex', justifyContent: 'space-between' }}>
                              <span>{job.job_number}</span>
                              {isActive && <span style={{ color: '#16a34a', fontSize: '12px' }}>⏱ Active</span>}
                            </div>
                            <div style={{ fontSize: '14px', color: '#666' }}>
                              {job.client_name || 'No client'}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div style={{ padding: '14px', color: '#999' }}>
                        {!jobsLoaded ? 'Loading jobs...' : jobSearch.length < 2 ? 'Type at least 2 characters to search...' : 'No jobs found'}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Phase Select - shows when job has phases */}
              {jobPhases.length > 0 && (
                <>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '13px', color: '#4338ca' }}>
                    🧩 Which phase? {jobPhases.length > 1 && <span style={{ color: '#dc2626' }}>*</span>}
                  </label>
                  <select value={selectedPhase} onChange={(e) => { setSelectedPhase(e.target.value); setSelectedWorkOrder(""); }}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px solid #6366f1', marginBottom: '10px', fontSize: '14px', minHeight: '40px', backgroundColor: '#eef2ff' }}>
                    <option value="">— Choose a phase —</option>
                    {jobPhases.map(p => <option key={p.id} value={p.id}>{p.name}{p.client_name ? ` — ${p.client_name}` : ''}</option>)}
                  </select>
                </>
              )}

              {/* Work Order Select - shows when job has work orders */}
              {visibleWorkOrders.length > 0 && (
                <>
                  <label style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontWeight: '500',
                    fontSize: '13px',
                    color: '#7c3aed'
                  }}>
                    📋 Select Work Order
                  </label>
                  <select
                    value={selectedWorkOrder}
                    onChange={(e) => setSelectedWorkOrder(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '2px solid #7c3aed',
                      marginBottom: '10px',
                      fontSize: '14px',
                      minHeight: '40px',
                      backgroundColor: '#f5f3ff'
                    }}
                  >
                    <option value="">Choose work order...</option>
                    {visibleWorkOrders.map(wo => (
                      <option key={wo.id} value={wo.id}>
                        {wo.order_number}: {wo.description} {wo.po_number ? `(PO: ${wo.po_number})` : ''}
                      </option>
                    ))}
                  </select>
                </>
              )}
              
              {loadingWorkOrders && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '8px', 
                  color: '#666',
                  marginBottom: '10px',
                  fontSize: '13px'
                }}>
                  Loading work orders...
                </div>
              )}

              {/* Work Type */}
              <label style={{ 
                display: 'block', 
                marginBottom: '6px', 
                fontWeight: '500',
                fontSize: '13px',
                color: '#555'
              }}>
                Work Type
              </label>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '6px',
                marginBottom: '12px'
              }}>
                {workTypes.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setSelectedWorkType(type.value)}
                    style={{
                      padding: '10px 6px',
                      backgroundColor: selectedWorkType === type.value ? '#e0e7ff' : '#f8fafc',
                      border: selectedWorkType === type.value ? '2px solid #2563eb' : '1px solid #e5e7eb',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      fontSize: '12px'
                    }}
                  >
                    {type.label}
                  </button>
                ))}
              </div>

              {/* Crew Selection */}
              {selectedJob && getAvailableCrewMembers().length > 0 && (
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px', 
                    fontWeight: '500',
                    fontSize: '13px',
                    color: '#555'
                  }}>
                    <FiUsers /> Clock In Crew Members (Optional)
                  </label>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap',
                    gap: '6px',
                    maxHeight: '80px',
                    overflowY: 'auto'
                  }}>
                    {getAvailableCrewMembers().map(member => (
                      <button
                        key={member.id}
                        onClick={() => toggleCrewMember(member.id)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: selectedCrewMembers.includes(member.id) ? '#dcfce7' : '#f8fafc',
                          border: selectedCrewMembers.includes(member.id) ? '2px solid #22c55e' : '1px solid #e5e7eb',
                          borderRadius: '20px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '13px'
                        }}
                      >
                        {selectedCrewMembers.includes(member.id) && (
                          <FiCheck size={12} color="#22c55e" />
                        )}
                        {member.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              {selectedJob && (
                <div style={{
                  backgroundColor: '#f0f9ff',
                  padding: '10px',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}>
                  <strong>Clocking in:</strong> {user?.name}
                  {selectedCrewMembers.length > 0 && (
                    <span>, {selectedCrewMembers.map(id => 
                      teamMembers.find(m => m.id === id)?.name
                    ).join(', ')}</span>
                  )}
                </div>
              )}
              </div>
              {/* End scrollable content area */}

              {/* Start Button - fixed at bottom */}
              <button
                onClick={handleJobStart}
                disabled={!selectedJob}
                style={{
                  width: '100%',
                  padding: '14px',
                  fontSize: '17px',
                  fontWeight: '600',
                  backgroundColor: selectedJob ? '#22c55e' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: selectedJob ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  minHeight: '50px',
                  flexShrink: 0,
                  marginTop: '10px'
                }}
              >
                <FiPlay size={20} /> 
                {selectedCrewMembers.length > 0 
                  ? `Start Timer (${selectedCrewMembers.length + 1} people)` 
                  : 'Start Timer'}
              </button>
            </div>
          </div>
        )}

        {/* Field Checklist - Overview (Mobile, shown on timer start) */}
        {fieldChecklistOverview && (
          <FieldChecklistModal
            jobId={fieldChecklistOverview.job_id}
            jobNumber={fieldChecklistOverview.job_number}
            mode="overview"
            onComplete={() => setFieldChecklistOverview(null)}
            onClose={() => setFieldChecklistOverview(null)}
          />
        )}
        {/* Field Checklist - Edit (Mobile, mid-job via clipboard button) */}
        {fieldChecklistEdit && (
          <FieldChecklistModal
            jobId={fieldChecklistEdit.job_id}
            jobNumber={fieldChecklistEdit.job_number}
            mode="edit"
            onComplete={() => setFieldChecklistEdit(null)}
            onClose={() => setFieldChecklistEdit(null)}
          />
        )}
        {/* Field Checklist - Clock Out (Mobile) */}
        {fieldChecklistJob && (
          <FieldChecklistModal
            jobId={fieldChecklistJob.job_id}
            jobNumber={fieldChecklistJob.job_number}
            mode="clockout"
            onComplete={handleFieldChecklistDone}
            onClose={handleFieldChecklistDone}
          />
        )}

        {/* Job Status Update Modal (Mobile) */}
        {statusUpdateJob && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 999999, padding: '16px'
          }}>
            <div style={{
              backgroundColor: 'white', borderRadius: '16px', padding: '20px',
              maxWidth: '380px', width: '100%', textAlign: 'center'
            }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '17px' }}>Update Job Status?</h3>
              <p style={{ color: '#666', margin: '0 0 12px', fontSize: '14px' }}>
                <strong>{statusUpdateJob.job_number}</strong>
              </p>
              <p style={{ color: '#999', margin: '0 0 14px', fontSize: '12px' }}>
                Currently: {statusUpdateJob.job_status}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px', maxHeight: '60dvh', overflowY: 'auto' }}>
                {jobStatusOptions.filter(opt => opt.status !== statusUpdateJob.job_status).map(opt => (
                  <button
                    key={opt.status}
                    onClick={() => handleStatusUpdate(opt.status)}
                    style={{
                      padding: '12px 14px', border: '2px solid #e5e7eb', borderRadius: '10px',
                      backgroundColor: 'white', cursor: 'pointer', fontSize: '14px',
                      fontWeight: '500', textAlign: 'left', display: 'flex',
                      alignItems: 'center', gap: '10px'
                    }}
                  >
                    <span style={{
                      width: '12px', height: '12px', borderRadius: '50%',
                      backgroundColor: opt.color, flexShrink: 0
                    }} />
                    {opt.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => goToCommentPrompt(statusUpdateJob)}
                style={{
                  padding: '10px 24px', backgroundColor: '#f5f5f5', border: '1px solid #ddd',
                  borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#666', width: '100%'
                }}
              >
                Skip — No Change
              </button>
            </div>
          </div>
        )}
        {commentPrompt && (
          <RequiredCommentModal jobId={commentPrompt.job_id} jobNumber={commentPrompt.job_number} workType={commentPrompt.work_type} onDone={handleCommentDone} />
        )}
      </div>
    );
  }

  // Desktop Layout (unchanged)
  return (
    <div className="time-tracking-page">
      <div className="page-header">
        <div>
          <h1><FiClock /> Time Clock</h1>
          <p className="subtitle">
            {currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
          {currentTime.toLocaleTimeString()}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', maxWidth: '1000px', margin: '0 auto' }}>
        
        {/* LEFT COLUMN: Daily Clock In/Out */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '25px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '10px', color: '#333' }}>
            <FiClock /> Daily Time
          </h2>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
            Clock in when you arrive, clock out when you leave. Used for payroll.
          </p>

          {!dailyEntry ? (
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleDailyClockIn}
                style={{
                  width: '100%',
                  padding: '20px',
                  fontSize: '20px',
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                <FiPlay /> Start Day
              </button>
            </div>
          ) : (
            <div>
              <div style={{
                textAlign: 'center',
                padding: '20px',
                backgroundColor: '#e8f5e9',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                  {onBreak ? '☕ On Break' : '⏱️ Time Today'}
                </div>
                <div style={{ 
                  fontSize: '36px', 
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  color: onBreak ? '#ff9800' : '#4caf50'
                }}>
                  {calculateElapsedTime(dailyEntry.clock_in)}
                </div>
              </div>

              {!onBreak ? (
                <button
                  onClick={handleStartBreak}
                  style={{
                    width: '100%',
                    padding: '15px',
                    fontSize: '18px',
                    backgroundColor: '#ff9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    marginBottom: '10px'
                  }}
                >
                  <FiCoffee /> Take Break
                </button>
              ) : (
                <button
                  onClick={handleEndBreak}
                  style={{
                    width: '100%',
                    padding: '15px',
                    fontSize: '18px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    marginBottom: '10px'
                  }}
                >
                  <FiPlay /> End Break
                </button>
              )}

              <button
                onClick={handleDailyClockOut}
                style={{
                  width: '100%',
                  padding: '15px',
                  fontSize: '18px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                <FiSquare /> End Day
              </button>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Job Time Tracking */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '25px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '10px', color: '#333' }}>
            <FiBriefcase /> Job Time
          </h2>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
            Track time on specific jobs. You can run multiple job timers at once.
          </p>

          {!dailyEntry && jobEntries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
              <FiBriefcase size={48} style={{ opacity: 0.3, marginBottom: '15px' }} />
              <p>Start your day first to track job time.</p>
            </div>
          ) : (
            <>
              {/* Active Job Timers - show first */}
              {jobEntries.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 10px', color: '#666', fontSize: '14px' }}>
                    Active Job Timers ({jobEntries.length})
                  </h4>
                  {jobEntries.map(entry => (
                    <div 
                      key={entry.clock_in_group || entry.id}
                      style={{
                        padding: '12px',
                        backgroundColor: entry.work_order_id ? '#f5f3ff' : '#e3f2fd',
                        borderRadius: '8px',
                        marginBottom: '10px',
                        border: entry.work_order_id ? '2px solid #7c3aed' : 'none'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <div>
                          <strong style={{ color: entry.work_order_id ? '#7c3aed' : '#1976d2' }}>
                            {entry.job_number}
                            {entry.work_order_number && (
                              <span style={{ fontWeight: '500', marginLeft: '6px' }}>
                                ({entry.work_order_number})
                              </span>
                            )}
                          </strong>
                          {entry.work_order_description && (
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#7c3aed',
                              marginTop: '2px'
                            }}>
                              📋 {entry.work_order_description}
                            </div>
                          )}
                          <div style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginTop: '4px'
                          }}>
                            <span style={{ 
                              fontSize: '12px', 
                              color: '#666',
                              backgroundColor: entry.work_order_id ? '#e9d5ff' : '#bbdefb',
                              padding: '2px 8px',
                              borderRadius: '4px'
                            }}>
                              {entry.work_type}
                            </span>
                            {entry.my_is_paused && (
                              <span style={{
                                fontSize: '12px',
                                color: '#f59e0b',
                                backgroundColor: '#fef3c7',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontWeight: '600'
                              }}>
                                ⏸️ PAUSED
                              </span>
                            )}
                          </div>
                          <div style={{ 
                            fontSize: '20px', 
                            fontFamily: 'monospace', 
                            marginTop: '4px',
                            color: entry.my_is_paused ? '#f59e0b' : 'inherit'
                          }}>
                            {calculateElapsedTime(entry.clock_in, entry)}
                          </div>
                          {entry.crew_members && entry.crew_members.length > 0 && (
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#475569',
                              marginTop: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <FiUsers size={12} />
                              {entry.crew_members.map((m, i) => (
                                <span key={m.id}>
                                  {m.name}{i < entry.crew_members.length - 1 ? ', ' : ''}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {/* Pause Crew / Resume Crew button - show when multiple crew members */}
                            {entry.crew_members && entry.crew_members.length > 1 && (
                              entry.my_is_paused ? (
                                <button
                                  onClick={() => handleResumeCrew(entry.job_id)}
                                  style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#22c55e',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                  }}
                                >
                                  <FiPlay /> Resume Crew
                                </button>
                              ) : (
                                <button
                                  onClick={() => handlePauseCrew(entry.job_id)}
                                  style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f59e0b',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                  }}
                                >
                                  <FiCoffee /> Pause Crew
                                </button>
                              )
                            )}
                            {/* Solo Pause/Resume button - show when single person */}
                            {entry.my_entry_id && (!entry.crew_members || entry.crew_members.length <= 1) && (
                              entry.my_is_paused ? (
                                <button
                                  onClick={() => handleResumeJob(entry.my_entry_id)}
                                  style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#22c55e',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                  }}
                                >
                                  <FiPlay /> Resume
                                </button>
                              ) : (
                                <button
                                  onClick={() => handlePauseJob(entry.my_entry_id)}
                                  style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f59e0b',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                  }}
                                >
                                  <FiCoffee /> Pause
                                </button>
                              )
                            )}
                            <button
                              onClick={() => handleJobStop(entry.job_id)}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: '#f44336',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px'
                              }}
                            >
                              <FiSquare /> Stop Crew
                            </button>
                            {FIELD_WORK_TYPES.includes(entry.work_type) && (
                              <button
                                onClick={() => setFieldChecklistEdit({ job_id: entry.job_id, job_number: entry.job_number })}
                                style={{
                                  padding: '8px 16px', backgroundColor: '#eff6ff',
                                  color: '#2563eb', border: '1px solid #bfdbfe',
                                  borderRadius: '6px', cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', gap: '5px'
                                }}
                              >
                                <FiClipboard /> Checklist
                              </button>
                            )}
                          </div>
                          {entry.my_entry_id && (
                            <button
                              onClick={() => handleLeaveJob(entry.my_entry_id)}
                              style={{
                                padding: '8px 16px',
                                backgroundColor: '#6b7280',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px'
                              }}
                            >
                              <FiLogOut /> Leave
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Job picker - only if daily entry exists */}
              {dailyEntry && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Select job:
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={jobSearch}
                    onChange={(e) => {
                      setJobSearch(e.target.value);
                      setShowJobDropdown(true);
                      if (!e.target.value) {
                        setSelectedJob('');
                      }
                    }}
                    onFocus={() => setShowJobDropdown(true)}
                    placeholder="Type to search jobs..."
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                      marginBottom: '10px'
                    }}
                    autoComplete="off"
                  />
                  {showJobDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '250px',
                      overflowY: 'auto',
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      marginTop: '-10px'
                    }}>
                      {filteredJobs.length > 0 ? (
                        filteredJobs.slice(0, 20).map(job => {
                          const isActive = jobEntries.some(e => e.job_id === job.id);
                          return (
                            <div
                              key={job.id}
                              onClick={() => !isActive && handleJobSelect(job)}
                              style={{
                                padding: '12px 15px',
                                cursor: isActive ? 'not-allowed' : 'pointer',
                                borderBottom: '1px solid #eee',
                                backgroundColor: isActive ? '#f5f5f5' : (selectedJob === job.id.toString() ? '#e3f2fd' : 'white'),
                                opacity: isActive ? 0.6 : 1
                              }}
                              onMouseEnter={(e) => !isActive && (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                              onMouseLeave={(e) => !isActive && (e.currentTarget.style.backgroundColor = selectedJob === job.id.toString() ? '#e3f2fd' : 'white')}
                            >
                              <div style={{ fontWeight: '500', display: 'flex', justifyContent: 'space-between' }}>
                                <span>{job.job_number}</span>
                                {isActive && <span style={{ color: '#16a34a', fontSize: '12px' }}>⏱ Active</span>}
                              </div>
                              <div style={{ fontSize: '13px', color: '#666' }}>
                                {job.client_name || 'No client'}
                              </div>
                              {job.property_address && (
                                <div style={{ fontSize: '12px', color: '#999' }}>
                                  {job.property_address}
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div style={{ padding: '12px 15px', color: '#999' }}>
                          No jobs found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Phase Select - shows when job has phases */}
                {jobPhases.length > 0 && (
                  <>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#4338ca' }}>
                      🧩 Which phase? {jobPhases.length > 1 && <span style={{ color: '#dc2626' }}>*</span>}
                    </label>
                    <select value={selectedPhase} onChange={(e) => { setSelectedPhase(e.target.value); setSelectedWorkOrder(""); }}
                      style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '2px solid #6366f1', marginBottom: '10px', fontSize: '16px', backgroundColor: '#eef2ff' }}>
                      <option value="">— Choose a phase —</option>
                      {jobPhases.map(p => <option key={p.id} value={p.id}>{p.name}{p.client_name ? ` — ${p.client_name}` : ''}</option>)}
                    </select>
                  </>
                )}

                {/* Work Order Select - shows when job has work orders */}
                {visibleWorkOrders.length > 0 && (
                  <>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#7c3aed' }}>
                      📋 Select work order:
                    </label>
                    <select 
                      value={selectedWorkOrder}
                      onChange={(e) => setSelectedWorkOrder(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '6px',
                        border: '2px solid #7c3aed',
                        marginBottom: '10px',
                        backgroundColor: '#f5f3ff'
                      }}
                    >
                      <option value="">Select a work order...</option>
                      {visibleWorkOrders.map(wo => (
                        <option key={wo.id} value={wo.id}>
                          {wo.order_number}: {wo.description} {wo.po_number ? `(PO: ${wo.po_number})` : ''}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                
                {loadingWorkOrders && (
                  <div style={{ textAlign: 'center', padding: '10px', color: '#666' }}>
                    Loading work orders...
                  </div>
                )}

                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Work type:
                </label>
                <select 
                  value={selectedWorkType}
                  onChange={(e) => setSelectedWorkType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    marginBottom: '15px'
                  }}
                >
                  {workTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>

                {/* Crew Selection - Desktop */}
                {selectedJob && getAvailableCrewMembers().length > 0 && (
                  <div style={{ 
                    marginBottom: '15px',
                    padding: '15px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '10px', 
                      fontWeight: '500',
                      fontSize: '14px'
                    }}>
                      <FiUsers /> Clock In Crew Members (Optional)
                    </label>
                    <div style={{ 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      {getAvailableCrewMembers().map(member => (
                        <button
                          key={member.id}
                          onClick={() => toggleCrewMember(member.id)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: selectedCrewMembers.includes(member.id) ? '#dcfce7' : 'white',
                            border: selectedCrewMembers.includes(member.id) ? '2px solid #22c55e' : '1px solid #ddd',
                            borderRadius: '20px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px'
                          }}
                        >
                          {selectedCrewMembers.includes(member.id) && (
                            <FiCheck size={14} color="#22c55e" />
                          )}
                          {member.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary */}
                {selectedJob && selectedCrewMembers.length > 0 && (
                  <div style={{
                    backgroundColor: '#f0f9ff',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    marginBottom: '15px',
                    fontSize: '13px',
                    color: '#0369a1'
                  }}>
                    <strong>Clocking in {selectedCrewMembers.length + 1} people:</strong> {user?.name}, {selectedCrewMembers.map(id => 
                      teamMembers.find(m => m.id === id)?.name
                    ).join(', ')}
                  </div>
                )}

                <button 
                  onClick={handleJobStart}
                  disabled={!selectedJob}
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    backgroundColor: selectedJob ? '#2196f3' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: selectedJob ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}
                >
                  <FiPlay /> 
                  {selectedCrewMembers.length > 0 
                    ? `Start Timer (${selectedCrewMembers.length + 1} people)` 
                    : 'Start Timer'}
                </button>
              </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Field Checklist - Overview (Desktop, shown on timer start) */}
      {fieldChecklistOverview && (
        <FieldChecklistModal
          jobId={fieldChecklistOverview.job_id}
          jobNumber={fieldChecklistOverview.job_number}
          mode="overview"
          onComplete={() => setFieldChecklistOverview(null)}
          onClose={() => setFieldChecklistOverview(null)}
        />
      )}
      {/* Field Checklist - Edit (Desktop, mid-job via clipboard button) */}
      {fieldChecklistEdit && (
        <FieldChecklistModal
          jobId={fieldChecklistEdit.job_id}
          jobNumber={fieldChecklistEdit.job_number}
          mode="edit"
          onComplete={() => setFieldChecklistEdit(null)}
          onClose={() => setFieldChecklistEdit(null)}
        />
      )}
      {/* Field Checklist - Clock Out (Desktop) */}
      {fieldChecklistJob && (
        <FieldChecklistModal
          jobId={fieldChecklistJob.job_id}
          jobNumber={fieldChecklistJob.job_number}
          mode="clockout"
          onComplete={handleFieldChecklistDone}
          onClose={handleFieldChecklistDone}
        />
      )}

      {/* Job Status Update Modal */}
      {statusUpdateJob && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 999999, padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '16px', padding: '24px',
            maxWidth: '420px', width: '100%', textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>Job Complete?</h3>
            <p style={{ color: '#666', margin: '0 0 16px', fontSize: '14px' }}>
              Update the status for <strong>{statusUpdateJob.job_number}</strong>
            </p>
            <p style={{ color: '#999', margin: '0 0 16px', fontSize: '13px' }}>
              Current status: <strong>{statusUpdateJob.job_status}</strong>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px', maxHeight: '60vh', overflowY: 'auto' }}>
              {jobStatusOptions.filter(opt => opt.status !== statusUpdateJob.job_status).map(opt => (
                <button
                  key={opt.status}
                  onClick={() => handleStatusUpdate(opt.status)}
                  style={{
                    padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '10px',
                    backgroundColor: 'white', cursor: 'pointer', fontSize: '14px',
                    fontWeight: '500', textAlign: 'left', display: 'flex',
                    alignItems: 'center', gap: '10px', transition: 'all 0.15s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = opt.color; e.currentTarget.style.backgroundColor = opt.color + '15'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.backgroundColor = 'white'; }}
                >
                  <span style={{
                    width: '12px', height: '12px', borderRadius: '50%',
                    backgroundColor: opt.color, flexShrink: 0
                  }} />
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => goToCommentPrompt(statusUpdateJob)}
              style={{
                padding: '10px 24px', backgroundColor: '#f5f5f5', border: '1px solid #ddd',
                borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#666'
              }}
            >
              Skip — No Change
            </button>
          </div>
        </div>
      )}
      {commentPrompt && (
        <RequiredCommentModal jobId={commentPrompt.job_id} jobNumber={commentPrompt.job_number} workType={commentPrompt.work_type} onDone={handleCommentDone} />
      )}
    </div>
  );
}

export default TimeTracking;